import React, { useEffect, useRef, useState } from 'react';
import { Slide, CarouselProject, CarouselStyle } from '@/types';
import { cn } from '@/lib/utils';
import { Loader2, AlertCircle } from 'lucide-react';
import TwitterSlide from '@/components/TwitterSlide';
import StorytellerSlide from '@/components/StorytellerSlide';

type JobState = 'pending' | 'running' | 'done' | 'failed';

interface Props {
  slide: Slide;
  index: number;
  project: CarouselProject;
  imageJobState?: JobState;
  selected?: boolean;
  onClick: () => void;
}

// Native size the real slide components are designed for.
const NATIVE_SLIDE_WIDTH = 1080;

export const SlideThumbnail: React.FC<Props> = ({
  slide,
  index,
  project,
  imageJobState,
  selected = false,
  onClick,
}) => {
  const SlideComponent =
    project.style === CarouselStyle.STORYTELLER ? StorytellerSlide : TwitterSlide;
  const showShimmer =
    slide.showImage &&
    (imageJobState === 'pending' || imageJobState === 'running') &&
    !slide.imageUrl;
  const showFailed = imageJobState === 'failed';

  // Measure the card width so we can scale the native-sized slide component
  // to fill it exactly. The card is `aspect-[4/5]` so height follows from width.
  const containerRef = useRef<HTMLButtonElement>(null);
  const [scale, setScale] = useState<number>(0.18);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const { width } = el.getBoundingClientRect();
      if (width > 0) setScale(width / NATIVE_SLIDE_WIDTH);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  return (
    <button
      ref={containerRef}
      type="button"
      onClick={onClick}
      className={cn(
        'group relative aspect-[4/5] w-full overflow-hidden rounded-lg border bg-muted/20 text-left transition-all',
        selected
          ? 'border-primary ring-2 ring-primary/50'
          : 'border-border hover:border-primary',
      )}
      aria-label={`Slide ${index + 1}`}
      aria-pressed={selected}
    >
      <div
        className="absolute top-0 left-0 origin-top-left pointer-events-none"
        style={{
          width: `${NATIVE_SLIDE_WIDTH}px`,
          height: `${NATIVE_SLIDE_WIDTH * 1.25}px`, // 4:5 aspect
          transform: `scale(${scale})`,
        }}
      >
        <SlideComponent
          slide={slide}
          profile={project.profile}
          index={index}
          total={project.slides.length}
          showSlideNumbers={project.showSlideNumbers}
          headerScale={project.headerScale}
          theme={project.theme}
          showVerifiedBadge={project.showVerifiedBadge}
          accentColor={project.accentColor}
          fontStyle={project.fontStyle}
          fontScale={project.fontScale}
          layoutSettings={project.layoutSettings}
        />
      </div>

      <div className="absolute top-1.5 left-1.5 bg-background/80 backdrop-blur rounded-md px-1.5 py-0.5 text-[10px] font-semibold z-10">
        {index + 1}
      </div>

      {showShimmer && (
        <div className="absolute inset-0 flex items-center justify-center bg-background/40 backdrop-blur-[1px] z-10">
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </div>
      )}

      {showFailed && (
        <div className="absolute inset-x-0 bottom-0 bg-destructive/80 text-destructive-foreground text-[10px] px-1.5 py-1 flex items-center gap-1 justify-center z-10">
          <AlertCircle className="h-3 w-3" />
          Falhou — clique para tentar
        </div>
      )}
    </button>
  );
};
